/**
 * SEO component for managing meta tags, Open Graph, and structured data
 */

import {useEffect} from 'react';
import {useLocation} from 'react-router-dom';

interface SEOProps {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  type?: string;
  keywords?: string[];
}

const defaultTitle = 'VoxaPilot - AI-Powered Meeting Assistant';
const defaultDescription = 'Transform your meetings with AI-powered transcription, intelligent summaries, and actionable insights. Record, transcribe, and analyze meetings automatically.';
const defaultImage = 'https://voxapilot.com/og-image.svg';
const siteUrl = 'https://voxapilot.com';

export function SEO({
  title = defaultTitle,
  description = defaultDescription,
  image = defaultImage,
  url,
  type = 'website',
  keywords = ['meeting transcription', 'AI meeting assistant', 'meeting summaries', 'voice to text', 'meeting analytics'],
}: SEOProps) {
  const location = useLocation();
  const currentUrl = url || `${siteUrl}${location.pathname}`;
  const fullTitle = title === defaultTitle ? title : `${title} | VoxaPilot`;

  useEffect(() => {
    // Update document title
    document.title = fullTitle;

    // Update or create meta tags
    const updateMetaTag = (name: string, content: string, attribute: string = 'name') => {
      let element = document.querySelector(`meta[${attribute}="${name}"]`) as HTMLMetaElement;
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    // Basic meta tags
    updateMetaTag('description', description);
    updateMetaTag('keywords', keywords.join(', '));
    updateMetaTag('author', 'VoxaPilot');
    updateMetaTag('robots', 'index, follow');

    // Open Graph tags
    updateMetaTag('og:title', fullTitle, 'property');
    updateMetaTag('og:description', description, 'property');
    updateMetaTag('og:image', image, 'property');
    updateMetaTag('og:url', currentUrl, 'property');
    updateMetaTag('og:type', type, 'property');
    updateMetaTag('og:site_name', 'VoxaPilot', 'property');

    // Twitter Card tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', fullTitle);
    updateMetaTag('twitter:description', description);
    updateMetaTag('twitter:image', image);

    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', currentUrl);

    // Structured data (JSON-LD)
    const structuredData = {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'VoxaPilot',
      applicationCategory: 'BusinessApplication',
      description: description,
      url: siteUrl,
      logo: image,
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD',
      },
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '4.8',
        ratingCount: '150',
      },
      featureList: [
        'AI-powered meeting transcription',
        'Intelligent meeting summaries',
        'Speaker detection',
        'Action items extraction',
        'Meeting analytics',
      ],
    };

    // Remove existing structured data
    const existingScript = document.querySelector('script[type="application/ld+json"]');
    if (existingScript) {
      existingScript.remove();
    }

    // Add new structured data
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(structuredData);
    document.head.appendChild(script);
  }, [title, description, image, currentUrl, type, keywords, fullTitle]);

  return null;
}

